import isEqual from "lodash/isEqual";
import { MultiActivityFormStoreProvider } from "PFApp/stores/activity_form/multi_roles_form";
import { Id } from "PFCore/types";
import { PropsWithChildren, useState } from "react";
import { createStore } from "zustand";
import { immer } from "zustand/middleware/immer";

import { ManageRolesStoreType, UnsavedEditStatus } from "./manage_roles_store.types";
import { ManageRolesStoreContext } from "./manage_roles_store_context";

export const ManageRolesStoreProvider = ({ children }: PropsWithChildren) => {
  const [store] = useState(
    createStore<ManageRolesStoreType>()(
      immer((set, get) => ({
        currentPage: 1,
        unsavedEdits: {},
        actions: {
          setCurrentPage: (page) =>
            set((state) => {
              state.currentPage = page;
            }),
          addUnsavedEdit: (
            roleId: Id,
            fieldName: string,
            value: unknown,
            previousValue: unknown,
            page: number,
            status: UnsavedEditStatus = "unsaved"
          ) =>
            set((state) => {
              const key = `${roleId}.${fieldName}`;
              const existing = state.unsavedEdits[key];
              if (existing && existing.status === "unsaved" && isEqual(existing.value, value)) {
                return;
              }
              // If an inflight or failed entry exists, we overwrite it with status "unsaved" so it will be
              // retried. previousValue is preserved from the original edit so that on failure we still
              // know the correct pre-edit server value.
              state.unsavedEdits[key] = {
                roleId,
                fieldName,
                value,
                previousValue: existing ? existing.previousValue : previousValue,
                status,
                isError: status === "failed" ? true : (existing?.isError ?? false),
                page
              };
            }),
          markInflight: (keys: string[]) =>
            set((state) => {
              keys.forEach((key) => {
                if (state.unsavedEdits[key]) {
                  state.unsavedEdits[key].status = "inflight";
                }
              });
            }),
          markFailed: (keys: string[]) =>
            set((state) => {
              keys.forEach((key) => {
                if (state.unsavedEdits[key]) {
                  state.unsavedEdits[key].status = "failed";
                  state.unsavedEdits[key].isError = false;
                }
              });
            }),
          markKeysAsErrored: (keys: string[]) =>
            set((state) => {
              keys.forEach((key) => {
                if (state.unsavedEdits[key]) {
                  state.unsavedEdits[key].isError = true;
                }
              });
            }),
          clearEdits: (keys: string[]) =>
            set((state) => {
              keys.forEach((key) => {
                delete state.unsavedEdits[key];
              });
            }),
          changeFailedEditsToUnsaved: (roleId: Id) =>
            set((state) => {
              Object.values(state.unsavedEdits).forEach((edit) => {
                if (edit.roleId === roleId && edit.status === "failed") {
                  edit.status = "unsaved";
                }
              });
            }),
          // Intentionally an action (not a selector) — reads state imperatively via get() without
          // subscribing, so callers don't re-render on every unsavedEdits mutation.
          hasUnsavedEdits: () => Object.values(get().unsavedEdits).some((edit) => edit.status === "unsaved")
        }
      }))
    )
  );

  return (
    <ManageRolesStoreContext.Provider value={store}>
      <MultiActivityFormStoreProvider>{children}</MultiActivityFormStoreProvider>
    </ManageRolesStoreContext.Provider>
  );
};
